import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Typography,
    Menu
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Delete, Edit, GetApp, PhotoLibrary } from "@material-ui/icons";
import { useAuth } from "../../../../../../features/auth/context";

import { SitePhotoDto, PHOTO_CATEGORIES, PHOTO_CATEGORY_LABELS, PhotoCategory, UpdateSitePhotoRequest } from "../../../../../../dtos/sitePhoto";
import { PhotoCard, EditPhotoDialog, FullScreenDialog } from "./components";
import { usePhotoGallery } from "./hooks";

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: "0px",
    },
    filterContainer: {
        marginBottom: "24px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
    },
    categoryFilter: {
        minWidth: "200px",
    },
    emptyState: {
        textAlign: "center",
        padding: "80px 20px",
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "12px",
        border: `2px dashed ${theme.palette.divider}`,
    },
    emptyIcon: {
        fontSize: "64px",
        marginBottom: "16px",
        opacity: 0.5,
    },
    gridContainer: {
        "& .MuiGrid-item": {
            display: "flex",
        }
    },
    deleteButton: {
        color: theme.palette.error.main,
        "&:hover": {
            backgroundColor: theme.palette.error.light + "20",
        }
    }
}));

interface Props {
    siteId: number;
    onRefresh?: () => void;
}

export const PhotoGallery = ({ siteId, onRefresh }: Props): JSX.Element => {
    const classes = useStyles();
    const { isSiteEmployee, isGlobalAdmin, isDealerAdmin, isSiteAdmin, isOrgAdmin } = useAuth();
    const scrollPositionRef = useRef<number>(0);
    const previousLoadingRef = useRef<boolean>(false);
    const isInitialLoadRef = useRef<boolean>(true);
    const isCategoryChangeRef = useRef<boolean>(false);

    const {
        photos,
        photoUrls,
        loading,
        selectedCategory,
        handleCategoryChange: originalHandleCategoryChange,
        updatePhotoMetadata,
        deletePhoto,
        downloadPhoto,
    } = usePhotoGallery({ siteId, onRefresh });

    const [fullScreenPhoto, setFullScreenPhoto] = useState<SitePhotoDto | null>(null);
    const [editPhoto, setEditPhoto] = useState<SitePhotoDto | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [selectedPhoto, setSelectedPhoto] = useState<SitePhotoDto | null>(null);

    const handleCategoryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        scrollPositionRef.current = window.scrollY;
        isCategoryChangeRef.current = true;
        originalHandleCategoryChange(event);
    };

    useEffect(() => {
        if (loading && !previousLoadingRef.current) {
            if (!isCategoryChangeRef.current && isInitialLoadRef.current) {
                isInitialLoadRef.current = false;
            } else if (!isCategoryChangeRef.current) {
                scrollPositionRef.current = window.scrollY;
            }
        }

        if (!loading && previousLoadingRef.current) {
            const shouldRestoreScroll = scrollPositionRef.current > 0 &&
                (!isInitialLoadRef.current || isCategoryChangeRef.current);

            if (shouldRestoreScroll) {
                requestAnimationFrame(() => {
                    window.scrollTo(0, scrollPositionRef.current);
                    scrollPositionRef.current = 0;
                    isCategoryChangeRef.current = false;
                });
            } else {
                isCategoryChangeRef.current = false;
            }
        }

        previousLoadingRef.current = loading;
    }, [loading]);

    const canDelete = isGlobalAdmin || isDealerAdmin || isSiteAdmin || isOrgAdmin;
    const canEdit = isSiteEmployee || isGlobalAdmin || isDealerAdmin || isSiteAdmin || isOrgAdmin;

    const handlePhotoClick = (photo: SitePhotoDto) => {
        setFullScreenPhoto(photo);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, photo: SitePhotoDto) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setSelectedPhoto(photo);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedPhoto(null);
    };

    const handleEditClick = () => {
        if (!selectedPhoto) return;
        setEditPhoto(selectedPhoto);
        handleMenuClose();
    };

    const handleEditSave = async (photoId: number, updateData: UpdateSitePhotoRequest) => {
        const success = await updatePhotoMetadata(photoId, updateData);
        if (success) {
            if (!photos.find((p: SitePhotoDto) => p.id === photoId)) {
                setFullScreenPhoto(null);
                setEditPhoto(null);
            }
        }
        return success;
    };

    const handleDeleteClick = async () => {
        if (!selectedPhoto) return;
        const success = await deletePhoto(selectedPhoto.id, selectedPhoto.fileName);
        if (success) {
            handleMenuClose();
            if (fullScreenPhoto && fullScreenPhoto.id === selectedPhoto.id) {
                setFullScreenPhoto(null);
            }
            if (editPhoto && editPhoto.id === selectedPhoto.id) {
                setEditPhoto(null);
            }
        }
    };

    const handleDownload = async () => {
        if (!selectedPhoto) return;
        await downloadPhoto(selectedPhoto.id, selectedPhoto.fileName);
        handleMenuClose();
    };

    if (loading) {
        return (
            <div className={classes.container}>
                <div className={classes.filterContainer}>
                    <Typography variant="body2" color="textSecondary">
                        Filter by category:
                    </Typography>
                    <FormControl className={classes.categoryFilter}>
                        <Select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            variant="outlined"
                        >
                            <MenuItem value="all">All Categories</MenuItem>
                            {PHOTO_CATEGORIES.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {PHOTO_CATEGORY_LABELS[category]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <Box display="flex" justifyContent="center" p={4}>
                    <Typography>Loading photos...</Typography>
                </Box>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.filterContainer}>
                <Typography variant="body2" color="textSecondary">
                    Filter by category:
                </Typography>
                <FormControl className={classes.categoryFilter}>
                    <Select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        variant="outlined"
                    >
                        <MenuItem value="all">All Categories</MenuItem>
                        {PHOTO_CATEGORIES.map((category) => (
                            <MenuItem key={category} value={category}>
                                {PHOTO_CATEGORY_LABELS[category]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {photos.length === 0 ? (
                <div className={classes.emptyState}>
                    <PhotoLibrary className={classes.emptyIcon} />
                    <Typography variant="h6" gutterBottom>
                        No photos found
                    </Typography>
                    <Typography variant="body2">
                        {selectedCategory === "all"
                            ? "Upload photos to document this site"
                            : `No photos found in the ${PHOTO_CATEGORY_LABELS[selectedCategory as PhotoCategory]} category`
                        }
                    </Typography>
                </div>
            ) : (
                <Grid container spacing={3} className={classes.gridContainer}>
                    {photos.map((photo: SitePhotoDto) => (
                        <Grid item xs={12} sm={12} md={6} lg={4} key={photo.id}>
                            <PhotoCard
                                photo={photo}
                                photoUrl={photoUrls[photo.id]}
                                onPhotoClick={handlePhotoClick}
                                onMenuOpen={handleMenuOpen}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            <Menu
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleDownload}>
                    <GetApp style={{ marginRight: "8px" }} />
                    Download
                </MenuItem>
                {canEdit && (
                    <MenuItem onClick={handleEditClick}>
                        <Edit style={{ marginRight: "8px" }} />
                        Edit
                    </MenuItem>
                )}
                {canDelete && (
                    <MenuItem onClick={handleDeleteClick} className={classes.deleteButton}>
                        <Delete style={{ marginRight: "8px" }} />
                        Delete
                    </MenuItem>
                )}
            </Menu>

            <FullScreenDialog
                open={Boolean(fullScreenPhoto)}
                photo={fullScreenPhoto}
                photoUrl={fullScreenPhoto ? photoUrls[fullScreenPhoto.id] : undefined}
                onClose={() => setFullScreenPhoto(null)}
            />

            <EditPhotoDialog
                open={Boolean(editPhoto)}
                photo={editPhoto}
                onClose={() => setEditPhoto(null)}
                onSave={handleEditSave}
            />
        </div>
    );
};